import React, { useCallback, useState } from 'react';
import { getUserInfo, getToken } from '@common/services';
import { useNavigate } from 'react-router-dom';
import { logout, goToLoginApp } from '@wisory-international-ab/login-frontend';

import { UserRole } from '@/constants';
import { useAppRedirectUrl } from '@/hooks';
import { trackUser } from '@/common/services/monitor';
import {
  getSelectedRoleStorage,
  removeGAEventObjectStorage,
  removeSelectedRoleStorage,
  setGAEventObjectStorage,
  setSelectedRoleStorage,
} from '@/common/utils/localstorage';

export interface AuthContextType {
  user: API.UserInfoModel | undefined;
  selectedRole?: UserRole;
  isSigning: boolean;
  isAdvisor: boolean;
  isDecisionMaker: boolean;
  isUser: boolean;
  goToLoginPage: () => void;
  signOut: () => void;
  setUser: (user: API.UserInfoModel) => void;
  tryToLogin: () => Promise<void>;
  getProfile: () => Promise<void>;
  navigateToApp: VoidFunction;
  setSelectedRole: (newRole: UserRole) => void;
}

export const AuthContext = React.createContext<AuthContextType>(null!);

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<API.UserInfoModel | undefined>(undefined);
  const [selectedRole, setSelectedRole] = useState<UserRole | undefined>();
  const [isSigning, setIsSigning] = useState(false);
  const redirectUrl = useAppRedirectUrl();

  const getProfile = async () => {
    const userInfo = await getUserInfo();
    const localStorageData = getSelectedRoleStorage(userInfo.id);
    let role = localStorageData || userInfo.roles[0];

    if (userInfo.roles.length > 1) {
      if (userInfo.roles.includes(UserRole.Advisor)) {
        role = UserRole.Advisor;
      } else if (userInfo.roles.includes(UserRole.DecisionMaker)) {
        role = UserRole.DecisionMaker;
      } else if (userInfo.roles.includes(UserRole.Admin)) {
        role = UserRole.Admin;
      } else {
        role = UserRole.User;
      }
    } else {
      role = UserRole.User;
    }

    role = localStorageData || role;

    // In case user role has been revoked - need to double check again
    if (!userInfo.roles.includes(role)) {
      removeSelectedRoleStorage(user?.id!!);
      setSelectedRole(userInfo.roles[0]);
    } else {
      setSelectedRoleStorage(userInfo.id!!, role);
      setSelectedRole(role);
    }

    setGAEventObjectStorage(userInfo?.id, {
      user_role: role,
      user_company: userInfo.company?.companyName || '',
    });

    trackUser({
      email: userInfo.email,
      id: userInfo.id,
      name: userInfo.fullName,
      company: userInfo?.company?.companyName,
      role,
    });

    setUser(userInfo);
  };

  const navigateToApp = () => {
    window.history.replaceState({}, document.title, window.location.href.split('?')[0]);
    navigate(redirectUrl, { replace: true });
  };

  const goToLoginPage = useCallback(() => {
    goToLoginApp();
  }, []);

  const tryToLogin = async () => {
    setIsSigning(true);

    const userSession = getToken();

    if (userSession) {
      await getProfile();
      setIsSigning(false);
      navigateToApp();
    }
  };

  const signOut = () => {
    setUser(undefined);
    removeSelectedRoleStorage(user?.id!!);
    removeGAEventObjectStorage(user?.id);
    logout();
  };

  const isAdvisor = selectedRole === UserRole.Advisor;
  const isDecisionMaker = selectedRole === UserRole.DecisionMaker;
  const isUser = selectedRole === UserRole.User;

  const value = {
    user,
    isSigning,
    isAdvisor,
    isDecisionMaker,
    isUser,
    selectedRole,
    setUser,
    tryToLogin,
    goToLoginPage,
    signOut,
    getProfile,
    navigateToApp,
    setSelectedRole: (newRole: UserRole) => {
      setSelectedRoleStorage(user?.id!!, newRole);
      setSelectedRole(newRole);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
